import { c as _c } from "react/compiler-runtime";
import { Forecast } from './Forecast';
import { ForecastTypeCombo, SubqTitleEdit } from './Subqs';
import { QuestionContext, zQuestionQuery } from './questionComponentTypes';
import { ActionIcon, useSafeMantineTheme } from '@mantine/core';
import { useQuestionForecasts, useQuestionForm } from './useQuestions';
import { match, P } from 'ts-pattern';
import { useQueryString } from 'utils/useQueryString';
import { pick } from 'remeda';
import { useContext, useState } from 'react';
import { Forecast as F, ForecastQuestionCfg } from 'shared/forecast';
import { Resolution as R } from 'shared/forecast/resolution';
import { QuestionCfg, QuestionResultsQuestion, zResolutionValue } from 'shared/forecast/question';
import { doSafe } from 'shared/promise';
import { TaskedStatus } from './Participation';
import { resultColorFn, ResultsQuery } from 'shared/forecast/results/QuestionResults';
import { type SubqDisplayMode } from './questionComponentTypes';
import { CommentsStats, useCommentStats } from 'components/discussion/Comments';
import { ResolutionValueEdit } from './Resolution';
import { UseForecastGroupApp } from './useForecasts';
import { ForecastFetched } from './useForecasts';
import { CardCollapsable } from 'components/Card';
import { ResolutionValueIcon } from 'shared/forecast/results/QuestionResults';
import { twMerge } from 'tailwind-merge';
import { QuestionCommonForm, QuestionEditDescription } from './QuestionForm';
import { MdHome } from 'react-icons/md';
import { QuestionTitle } from './QuestionTitle';
import { EditingButton } from 'components/form/EditingButton';
import z from 'zod';
import { useCommentExpand } from 'components/discussion/comments';
import { isQuestionTasked } from './participation';
import { usePortal } from 'components/portal/portal';

/** Similar to subquestions tiles, but for the question */
export const QuestionEditForecast = t0 => {
  const $ = _c(18);
  const {
    displayMode,
    className,
    options: t1
  } = t0;
  const {
    disableQuestionForecast
  } = t1;
  const {
    profile
  } = usePortal();
  const {
    question_id,
    isOwner,
    readonly
  } = useContext(QuestionContext);
  let t2;
  if ($[0] !== question_id) {
    t2 = {
      question_id,
      pick: _temp
    };
    $[0] = question_id;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    form: qForm,
    question: questionRaw
  } = useQuestionForm(t2);
  const q = {
    ...questionRaw,
    cfg: {
      ...questionRaw.cfg,
      disableQuestionForecast
    }
  };
  let t3;
  if ($[2] !== question_id) {
    t3 = {
      question_id
    };
    $[2] = question_id;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const commentStats = useCommentStats(t3);
  const {
    colors
  } = useSafeMantineTheme();
  const resultsColor = resultColorFn({
    colors,
    cfg: q?.cfg.colors
  });
  const [editDescription, setEditDescription] = useState(false);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = z.object(pick(ResultsQuery.zQueryShape, ["compare_ts"])).merge(zQuestionQuery.pick({
      editingQuestion: true,
      showQuestionForecast: true,
      subquestion: true
    }));
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const {
    query,
    setQuery
  } = useQueryString(t4);
  let t5;
  if ($[5] !== query.compare_ts || $[6] !== question_id) {
    t5 = {
      question_id,
      timestamps: query.compare_ts
    };
    $[5] = query.compare_ts;
    $[6] = question_id;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    forecasts
  } = useQuestionForecasts(t5);
  const {
    setCommentExpand
  } = useCommentExpand();
  const t6 = disableQuestionForecast ? "w-[60em] max-w-full" : "w-full flex justify-center";
  let t7;
  if ($[8] !== className || $[9] !== t6) {
    t7 = twMerge(t6, className);
    $[8] = className;
    $[9] = t6;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== editDescription || $[12] !== qForm || $[13] !== readonly) {
    t8 = t9 => {
      const {
        values
      } = t9;
      return <QuestionEditDescription value={values.description} onChange={v => qForm.setFieldValue("description", v)} readonly={readonly} editing={editDescription} setEditing={setEditDescription} />;
    };
    $[11] = editDescription;
    $[12] = qForm;
    $[13] = readonly;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t10 = match({
    values: qForm.values,
    q,
    disableQuestionForecast
  }).with({
    values: P.not(P.nullish),
    disableQuestionForecast: true,
    q: P.not(P.nullish)
  }, t8).with({
    values: P.not(P.nullish),
    q: P.not(P.nullish)
  }, t9 => {
    const {
      values: values_0,
      q: q_1
    } = t9;
    const resolution = F.getQuestionResolutionForecast({
      question: {
        ...(q_1.cfg as any as QuestionResultsQuestion),
        id: question_id
      },
      getForecasts: () => forecasts
    });
    const editing = query.editingQuestion == question_id;
    const setEditing = v_0 => v_0 ? setQuery({
      editingQuestion: question_id
    }) : setQuery({
      editingQuestion: undefined
    });
    const showForecast = query.showQuestionForecast ?? true;
    const isTasked = isQuestionTasked(profile, q_1?.cfg);
    return resolution && displayMode != "order" && <div className="grow relative border p-2 rounded-lg flex flex-col gap-2 group max-w-[calc(min(100%,60em))]"><div className="flex items-start gap-2"><div className="flex-grow">{editing ? <><SubqTitleEdit form={qForm} /></> : <div className=""><QuestionTitle title={<div className="flex items-center gap-2"><MdHome className="shrink-0 icon-text" /><div>{q_1.cfg.title}</div></div>} type="question" heightMode={showForecast ? "long" : "short"} {...pick(q_1.cfg, ["subTitle", "description"])} /></div>}</div>{!readonly && <EditingButton className={twMerge(editing ? "" : "group-hover:opacity-100 opacity-0")} editing={editing} setEditing={setEditing} isDirty={qForm.isDirty} />}</div><div className="flex items-center gap-2"><ForecastTypeCombo value={values_0.forecastType} onChange={t => qForm.setFieldValue("forecastType", t)} existingForecasts={forecasts?.forecasts?.length} readonly={!editing} /><ActionIcon onClick={() => {
          setQuery({
            subquestion: "_"
          });
          setCommentExpand(true);
        }}><CommentsStats {...commentStats} /></ActionIcon><TaskedStatus isTasked={isTasked} progress={{
          total: 1,
          completed: doSafe(resolution.group.myForecast, _temp2) ? 1 : 0
        }} /></div>{editing && <QuestionCommonForm readonly={false} form={qForm} isOwner={isOwner} existingForecasts={forecasts?.forecasts?.length} />}{match(displayMode).with("forecast", () => <CardCollapsable opened={showForecast} onChange={showQuestionForecast => setQuery({
        showQuestionForecast
      })} classNames={{
        title: "bg-transparent"
      }} title={<div className="flex items-center gap-2"><div className="grow">Forecast</div>{(() => {
          const key = R.resolutionKeyString({
            type: values_0.forecastType
          });
          const resValue = qForm.values?.resolutionValues[key];
          return resValue && resValue.status != "pending" ? <div className=""><ResolutionValueIcon {...resValue} /></div> : null;
        })()}</div>}><Forecast question={values_0 as ForecastQuestionCfg} color={t_0 => resultsColor(t_0 == "default" ? "question" : t_0)} resolution={resolution} {...{
          qForm,
          showComparison: editing
        }} /></CardCollapsable>).with("resolution", () => {
        const key_0 = R.resolutionKeyString({
          type: values_0.forecastType
        });
        const resValue_0 = qForm.values?.resolutionValues[key_0] ?? zResolutionValue.parse({});
        return <ResolutionValueEdit className="pt-2" type={values_0.forecastType} {...resValue_0} onChange={v_1 => qForm.setFieldValueDynamic(["resolutionValues", key_0], v_1)} />;
      }).exhaustive()}</div>;
  }).otherwise(_temp3);
  let t11;
  if ($[15] !== t10 || $[16] !== t7) {
    t11 = <div className={t7}>{t10}</div>;
    $[15] = t10;
    $[16] = t7;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
};
function _temp(cfg) {
  return pick(cfg, ["forecastType", "unitLabel", "unitLabelLong", "defaultBounds", "hardBounds", "publish", "numberFormat", "title", "subTitle", "colors", "amountType", "chart", "comparisons", "description", "tasked", "resolutionValues"]);
}
function _temp2(f) {
  return F.shouldShow(f);
}
function _temp3() {
  return <></>;
}